import { CalendarIcon } from "@heroicons/react/24/outline";
import { useState } from "react";

import { cn } from "@fronterahealth/frontera-ui-components/lib/utils";
import { Button } from "@fronterahealth/frontera-ui-components/ui/button";
import { Calendar } from "@fronterahealth/frontera-ui-components/ui/calendar";
import { inputVariants } from "@fronterahealth/frontera-ui-components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@fronterahealth/frontera-ui-components/ui/popover";

import { ApiReportSectionFieldsFieldTypeChoices } from "@api/graphql/types-and-hooks";
import { ReportFieldDisabledValue } from "@components/ReportField/ReportFieldDisabledValue";
import {
  formatDisplayDate,
  formatStoredDate,
  isDateFieldContentValid,
  parseStoredDate,
} from "@components/ReportField/inputs/dateFieldUtils";
import { ReportFieldInputBaseProps } from "@components/ReportField/types";

const tenYearsFromNow = new Date();
tenYearsFromNow.setFullYear(tenYearsFromNow.getFullYear() + 10);

export const ReportFieldDate: React.FC<ReportFieldInputBaseProps> = ({
  id,
  value,
  onChange,
  variant,
  placeholder,
  disabled,
  onFocus,
  onBlur,
  ariaInvalid,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const selected = parseStoredDate(value);
  const display = formatDisplayDate(selected);
  // A picked date or an unreadable leftover; an unresolved `{dateOfReport}` is left for the server to fill.
  const canClear = !!selected || !isDateFieldContentValid(ApiReportSectionFieldsFieldTypeChoices.Date, null, value);

  if (disabled) {
    return <ReportFieldDisabledValue value={display || value} />;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            id={id}
            type="button"
            variant="outline"
            onFocus={onFocus}
            onBlur={onBlur}
            aria-invalid={ariaInvalid}
            className={cn(
              inputVariants({ variant }),
              "w-full justify-between font-normal",
              !display && !value && "text-static-text-subtle",
              className,
            )}
          >
            {display || value || placeholder || "MM/DD/YYYY"}
            <CalendarIcon
              className={cn(
                "size-4 shrink-0 opacity-60 transition-opacity group-hover/button:opacity-60 group-focus-visible/button:opacity-60 group-aria-expanded/button:opacity-60",
                variant === "ghost" && "opacity-0",
              )}
            />
          </Button>
        }
      />
      <PopoverContent align="end" className="w-auto p-0">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          captionLayout="dropdown"
          endMonth={tenYearsFromNow}
          onSelect={(day) => {
            if (!day) return;
            onChange(formatStoredDate(day));
            setOpen(false);
          }}
        />
        {canClear && (
          <div className="flex justify-end border-t p-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                void onChange("");
                setOpen(false);
              }}
            >
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
